import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@/ui/components/atoms/Loading';
import { ControlsContainer } from '@/ui/components/molecules/ControlsContainer';
import { ExpertiseList } from '@/ui/components/molecules/ExpertiseList';
import type { ExpertiseStatus } from '@/ui/components/molecules/ExpertiseCard/ExpertiseCard.types';
import { useExpertDossiers } from '../../hooks/useExpertDossiers';
import type { Expert } from '../../types/expert.types';
import './ExpertDossiers.styles.css';

interface ExpertDossiersProps {
  expert: Expert;
  onBack: () => void;
}

export const ExpertDossiers: React.FC<ExpertDossiersProps> = ({ 
  expert,
  onBack 
}) => {
  const navigate = useNavigate();
  
  // Detect the current role context from the URL
  const currentPath = window.location.pathname;
  const isBureauContext = currentPath.includes('/bureau/');
  const baseRoute = isBureauContext ? '/bureau' : '/admin';
  
  const {
    dossiers,
    expertName,
    total,
    stats,
    isLoading,
    error,
    filters,
    updateFilter,
    refetch
  } = useExpertDossiers(expert.nom_traitant);

  const handleExpertiseClick = (expertiseId: number | string) => {
    navigate(`${baseRoute}/expertise/${expertiseId}`, { 
      state: { 
        from: `${baseRoute}/expert`,
        expertContext: expert // Pass the expert context to return to their dossiers
      } 
    });
  };

  if (isLoading) {
    return (
      <div className="expert-dossiers-container">
        <Loading 
          size="large" 
          text="Chargement des dossiers..."
          fullScreen={false}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="expert-dossiers-container">
        <div className="expert-dossiers-error">
          {error instanceof Error ? error.message : 'Une erreur est survenue'}
        </div>
      </div>
    );
  }

  // Transform dossiers data for ExpertiseList
  const expertiseListData = dossiers.map(dossier => {
    const tags = [];
    
    // Add days since RV tag if urgent
    if (dossier.isUrgent && dossier.urgencyReason) {
      tags.push(dossier.urgencyReason);
    } else if (dossier.daysSinceRv !== null && dossier.daysSinceRv >= 0) {
      tags.push(`${dossier.daysSinceRv} jour${dossier.daysSinceRv !== 1 ? 's' : ''} depuis RV`);
    }

    // Add payment status tag
    if (dossier.perception) {
      if (dossier.perception === 'PAYE BANQUE' || 
          dossier.perception?.toLowerCase().includes('preuve virement')) {
        tags.push('Payé');
      } else if (dossier.perception?.toLowerCase().includes('facturer')) {
        tags.push('Facturé');
      } else if (dossier.perception === 'En attente') {
        tags.push('Attente paiement');
      }
    }
    
    // Add invoice status if relevant
    if (dossier.facture_emise === 'oui' || dossier.facture_emise === 'emise') {
      tags.push('Facture émise');
    }

    // Build full client name with contact info
    let clientDisplay = dossier.client?.name || 'Client non renseigné';
    if (dossier.client?.phone) {
      clientDisplay += ` - ${dossier.client.phone}`;
    }
    if (dossier.client?.email) {
      clientDisplay += ` - ${dossier.client.email}`;
    }

    return {
      id: String(dossier.id),
      reference: String(dossier.id),
      title: dossier.type || 'Expertise immobilière',
      status: 'en_cours' as ExpertiseStatus,
      statusDisplay: dossier.status || 'status not found',
      variant: 'standard' as const,
      clientName: clientDisplay,
      expertName: dossier.expert || expertName,
      createdAt: dossier.date || undefined,
      updatedAt: dossier.date_du_rv || undefined,
      dueDate: dossier.date_du_rv || undefined,
      address: dossier.address ? {
        street: dossier.address.join(', '),
        city: undefined,
        postalCode: undefined,
        country: 'France'
      } : undefined,
      amount: typeof dossier.price === 'string' ? parseFloat(dossier.price) : dossier.price,
      currency: 'EUR',
      paid: dossier.perception === 'PAYE BANQUE' || 
            dossier.perception?.toLowerCase().includes('preuve virement'),
      perception: dossier.perception,
      invoiceStatus: dossier.affaire_facturee === 'oui' ? 'emise' : undefined,
      invoiceIssued: dossier.facture_emise === 'oui' || dossier.facture_emise === 'emise',
      progress: 50,
      stage: dossier.status || 'Expert à achever',
      stageId: 0,
      priority: dossier.isUrgent ? 'urgent' as const : 'medium' as const,
      tags,
      category: 'expertise',
      type: dossier.type || 'Expertise immobilière',
      description: dossier.heure_rv ? `RV à ${dossier.heure_rv.substring(0, 5)}` : undefined,
      notes: dossier.timeSince || undefined,
      documentsCount: undefined,
      messagesCount: undefined,
      tasksCompleted: undefined,
      tasksPending: undefined,
      className: dossier.isUrgent ? 'expert-dossier-urgent' : undefined,
      showActions: true,
      selectable: false,
      selected: false,
      disabled: false,
      loading: false
    };
  });

  return (
    <div className="expert-dossiers-page-container">
      {/* Header - Matching payment-header style */}
      <div className="expert-dossiers-header">
        <div className="expert-dossiers-header-content">
          <div className="expert-dossiers-title-row">
            <div className="expert-dossiers-title-left">
              <button 
                className="expert-dossiers-back-btn"
                onClick={onBack}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 20 20" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M12.5 15L7.5 10L12.5 5" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                Retour
              </button>
              <div className="expert-dossiers-title-content">
                <h1 className="expert-dossiers-title">
                  Dossiers de {expertName}
                </h1>
                <p className="expert-dossiers-subtitle">
                  {total} dossier{total > 1 ? 's' : ''} à achever
                </p>
              </div>
            </div>
            <div className="expert-dossiers-action-wrapper">
              {/* Space for future action buttons if needed */}
            </div>
          </div>
          {stats && (
            <div className="expert-dossiers-stats">
              <span className="expert-dossiers-stat">
                Total assigné: <strong>{stats.total_assigned}</strong>
              </span>
              <span className="expert-dossiers-stat">
                Cette semaine: <strong>{stats.this_week_appointments}</strong>
              </span>
              <span className="expert-dossiers-stat">
                Semaine prochaine: <strong>{stats.next_week_appointments}</strong>
              </span>
              {stats.paid !== undefined && stats.unpaid !== undefined && (
                <>
                  <span className="expert-dossiers-stat">
                    Payés: <strong>{stats.paid}</strong>
                  </span>
                  <span className="expert-dossiers-stat">
                    Non payés: <strong>{stats.unpaid}</strong>
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="expert-dossiers-content">
        <div className="expert-dossiers-content-inner">
          {/* Controls and filters */}
          <div className="expert-dossiers__controls-wrapper">
          <ControlsContainer
            searchQuery={filters.searchQuery}
            onSearchChange={(value) => updateFilter('searchQuery', value)}
            sortBy={filters.sortBy}
            onSortChange={(value) => updateFilter('sortBy', value)}
            sortOrder={filters.sortOrder}
            onSortOrderChange={(value) => updateFilter('sortOrder', value)}
            sortOptions={[
              { value: 'date', label: 'Date demande' },
              { value: 'date_rv', label: 'Date visite' },
              { value: 'id', label: 'Numéro' },
              { value: 'status', label: 'Statut' }
            ]}
            activeFilter={filters.activeFilter}
            onFilterChange={(value) => updateFilter('activeFilter', value)}
            filterOptions={[
              { key: 'all', label: 'Tous' },
              { key: 'urgent', label: 'Urgent' },
              { key: 'paid', label: 'Payé' },
              { key: 'unpaid', label: 'Non payé' }
            ]}
            showFilters={true}
            resultsCount={dossiers.length}
            searchPlaceholder="Rechercher par adresse, client, numéro..."
            className="expert-dossiers__controls-container"
          />
        </div>

        {/* Dossiers list */}
        <ExpertiseList
          expertises={expertiseListData}
          onExpertiseClick={(expertise) => handleExpertiseClick(expertise.id)}
          showFacadeImage={true}
          showProgress={true}
          showActions={true}
          emptyStateTitle="Aucun dossier à achever"
          emptyStateMessage={
            filters.searchQuery || filters.activeFilter !== 'all'
              ? 'Aucun dossier ne correspond aux critères de recherche'
              : `Aucun dossier à achever pour ${expertName}`
          }
          isSearchActive={!!filters.searchQuery || filters.activeFilter !== 'all'}
          className="expert-dossiers-list"
        />
        </div>
      </div>
    </div>
  );
};